import React, {useState, useEffect, useCallback, useRef} from 'react';
import Fuse from 'fuse.js';
import './chat.css';
import Div from '../components/Div.styled';
import {config} from '../environment';
import useMediaQuery from '../hooks/useMediaQuery';
import TypographyHeading from '../components/Typography/TypographyHeading';
import {TextAlignment} from '@volkswagen-onehub/components-core';
import ChatTextField from '../components/ChatTextField';
import LottieButtonChatCTA from '../components/LottieButtonChatCTA';
import ChatWindow from './ChatWindow';
import {
  ChatPromptItem,
  ChatPromptsDropdown,
  ChatPromptsDropdownItem,
} from './styles';
import useAmbit from './useAmbit';
import {Prompt} from './types';
import bodyScrollLock from '../utils/bodyScrollLock';

const EveeChatInputLanding: React.FC = () => {
  const {isMobile} = useMediaQuery();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<{label: string; value: string}[]>([]);
  const [keyPress, setKeyPress] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const {sendMessage, loading, messages} = useAmbit({
    open: isOpen,
  });
  const promptSearchInstance = useRef<Fuse<Prompt>>();

  const openModal = useCallback(() => {
    setIsOpen(true);
    bodyScrollLock.enable();
    inputRef.current?.blur();
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setOptions([]);
    setKeyPress('');
    setInputValue('');
    bodyScrollLock.disable();
  }, []);

  const createSearchInstance = useCallback((data: Prompt[]) => {
    const fuseOptions = {
      keys: ['prompt', 'keywords'],
      threshold: 0.4,
    };

    promptSearchInstance.current = new Fuse(data, fuseOptions);
  }, []);

  const fetchPrompts = useCallback(async () => {
    try {
      const promptsResponse = await fetch(`${config.API_EV_CHAT_PROMPTS}`, {
        headers: {'Content-Type': 'application/json'},
      });
      const {data}: {data: Prompt[] | null} = await promptsResponse.json();

      if (data && data.length > 0) {
        createSearchInstance(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [createSearchInstance]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (promptSearchInstance.current) {
      const suggestionOptions = promptSearchInstance.current.search(value);

      setOptions(
        suggestionOptions.map((result) => ({
          label: result.item.prompt,
          value: result.item.prompt,
        })),
      );
    }
  };

  const handleOptionSelection = useCallback(
    (option: {label: string; value: string}) => {
      setInputValue(option.label);
      sendMessage(option.label);
      openModal();
    },
    [openModal, sendMessage],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') setKeyPress(event.key);
    },
    [],
  );

  useEffect(() => {
    if (keyPress === 'Enter' && inputValue && !isOpen) {
      sendMessage(inputValue);
      openModal();
    }
  }, [keyPress, inputValue, isOpen, sendMessage, openModal]);

  const renderSuggestions = useCallback(() => {
    if (options.length === 0 || !inputValue) return null;
    return (
      <ul>
        {options.map((option) => (
          <ChatPromptsDropdownItem
            key={`${option.value}-${option.label.replace(' ', '-')}`}
            onClick={() => handleOptionSelection(option)}
          >
            <ChatPromptItem>{option.label}</ChatPromptItem>
          </ChatPromptsDropdownItem>
        ))}
      </ul>
    );
  }, [options, handleOptionSelection, inputValue]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  return (
    <>
      <Div padding="100px 0 0 0">
        <Div customStyles={{position: 'relative', paddingBottom: 160}}>
          <Div
            customStyles={{
              position: 'relative',
              backgroundImage: isMobile
                ? `url(${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}evw_fit_background_mobile.png)`
                : `url(${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}evw_fit_background_desktop.png)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              textAlign: 'center',
              paddingTop: isMobile ? 80 : 120,
              transformOrigin: 'center bottom',
              height: isMobile ? 540 : 720,
            }}
          >
            <Div
              maxWidth={isMobile ? '326px' : '551px'}
              margin="0 auto"
              customStyles={{height: '100%'}}
            >
              <Div
                customStyles={{
                  maxWidth: isMobile ? '326px' : '530px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '50%',
                }}
              >
                <LottieButtonChatCTA
                  width={isMobile ? 64 : 80}
                  height={isMobile ? 64 : 80}
                />
                <TypographyHeading
                  textAlign={TextAlignment.center}
                  style={{
                    fontSize: isMobile ? '32px' : '48px',
                    marginTop: '16px',
                  }}
                >
                  Hi, I’m eVee!
                </TypographyHeading>
                <TypographyHeading
                  textAlign={TextAlignment.center}
                  style={{
                    fontSize: isMobile ? '32px' : '48px',
                  }}
                  bold
                >
                  Ask me anything
                </TypographyHeading>
                <ChatTextField
                  ref={inputRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  isMobile={isMobile}
                  marginTop={isMobile ? '64px' : '40px'}
                  placeholder="e.g. 'What is the range of an EV"
                  onKeyDown={handleKeyPress}
                />
                <ChatPromptsDropdown
                  style={{width: isMobile ? '326px' : '530px'}}
                >
                  {renderSuggestions()}
                </ChatPromptsDropdown>
              </Div>
            </Div>
          </Div>
        </Div>
      </Div>
      <ChatWindow
        active={isOpen}
        onClose={closeModal}
        messages={messages}
        handleSendMessage={sendMessage}
        loading={loading}
      />
    </>
  );
};

export default EveeChatInputLanding;
